// Lia's room: a pro streamer setup drawn in pure three.js. React talks to it only through
// the handle createStudioScene returns. The drawing buffer is a fixed 16:9 (STAGE_W ×
// STAGE_H) because MediaRecorder tapes the buffer, not the CSS box — the canvas is then
// scaled to fit whatever column it sits in.
//
// Lia herself is a faceted procedural rig inside a GLB slot: drop a Blender export at
// /models/influio-tv/lia.glb (head node named "Head", optional "Mouth") and she is
// replaced without touching show logic. The rig is jointed (shoulder → elbow → wrist per
// arm) so gestures are rotations about joints, not floating capsules. The camera is not a
// viewport, it is a director: each Segment kind has its own shot list (see GRAMMAR) and a
// cut happens on every Segment change; between cuts the camera dollies slowly and breathes
// like a handheld.
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

export const STAGE_W = 1280;
export const STAGE_H = 720;
const BG = 0x0a0913;
const LOGO_URL = '/images/influio_logo.png';
const LIA_GLB = '/models/influio-tv/lia.glb';

const LIA_POS = new THREE.Vector3(0, 0, -1.35);
const HEAD_Y = 1.86;
const SCREEN_POS = new THREE.Vector3(2.15, 2.25, -3.05);
const SCREEN_W = 3.4;
const SCREEN_H = SCREEN_W * 9 / 16;
const FONT = 'Inter, system-ui, sans-serif';

const flat = (color, opts = {}) => new THREE.MeshStandardMaterial({
    color, roughness: opts.roughness ?? 0.9, metalness: opts.metalness ?? 0.02, flatShading: true,
    emissive: opts.emissive ?? 0x000000, emissiveIntensity: opts.intensity ?? 1,
});
const glow = (color, intensity = 1.6) => new THREE.MeshStandardMaterial({
    color, emissive: color, emissiveIntensity: intensity, roughness: 1, metalness: 0,
});
function box(parent, w, h, d, mat, x = 0, y = 0, z = 0) {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
    m.position.set(x, y, z);
    m.castShadow = true;
    m.receiveShadow = true;
    parent.add(m);
    return m;
}
function cyl(parent, rTop, rBot, h, mat, x = 0, y = 0, z = 0, seg = 12) {
    const m = new THREE.Mesh(new THREE.CylinderGeometry(rTop, rBot, h, seg), mat);
    m.position.set(x, y, z);
    m.castShadow = true;
    parent.add(m);
    return m;
}
function sphere(parent, r, mat, x = 0, y = 0, z = 0, ws = 10, hs = 8) {
    const m = new THREE.Mesh(new THREE.SphereGeometry(r, ws, hs), mat);
    m.position.set(x, y, z);
    m.castShadow = true;
    parent.add(m);
    return m;
}
function capsule(parent, r, len, mat, x = 0, y = 0, z = 0) {
    const m = new THREE.Mesh(new THREE.CapsuleGeometry(r, len, 2, 8), mat);
    m.position.set(x, y, z);
    m.castShadow = true;
    parent.add(m);
    return m;
}
function joint(parent, x, y, z) {
    const g = new THREE.Group();
    g.position.set(x, y, z);
    parent.add(g);
    return g;
}
const ease = (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
const clamp01 = (v) => Math.max(0, Math.min(1, v));

/* ---------- the wall screen: one canvas texture, repainted per Segment ---------- */

const TL = (n, digits = 0) => `₺${(Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
const shortTitle = (t) => (t || '')
    .replace(/\b(Karaca|Ninja|RCR|Emsan)\b/gi, '').replace(/\d+\s?(ml|cm|mm|lt|w|parça|kişilik|lı|li)\b/gi, '')
    .replace(/\s{2,}/g, ' ').trim().split(' ').slice(0, 5).join(' ');
const RANK = ['#ffd166', '#c0c0c0', '#cd7f32'];

function wrap(ctx, text, maxWidth) {
    const words = String(text || '').split(/\s+/);
    const lines = [];
    let line = '';
    for (const w of words) {
        const test = line ? `${line} ${w}` : w;
        if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; } else line = test;
    }
    if (line) lines.push(line);
    return lines;
}
function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

function makeBoard(loadImage) {
    const canvas = document.createElement('canvas');
    canvas.width = 1280;
    canvas.height = 720;
    const ctx = canvas.getContext('2d');
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    const W = canvas.width, H = canvas.height;
    // product photos by catalog URL, fetched once through loadImage (the CDN has no CORS,
    // so the studio routes them via the server); null = tried and missing
    const images = new Map();

    /** Loads every product photo in the facts; resolves when the boards can paint them. */
    const prepare = async (facts) => {
        if (!loadImage) return;
        const items = [...(facts?.topProducts || []), ...(facts?.topInfluencers || []).map((i) => i.topProduct)]
            .filter((p) => p?.image && !images.has(p.image));
        await Promise.all(items.map(async (p) => {
            images.set(p.image, null);
            try { images.set(p.image, await loadImage(p.image)); } catch (e) { /* a dead catalog link is a card without a photo */ }
        }));
    };

    const frame = (title) => {
        const g = ctx.createLinearGradient(0, 0, W, H);
        g.addColorStop(0, '#14282d');
        g.addColorStop(1, '#233d40');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
        ctx.strokeStyle = 'rgba(255,255,255,0.04)';
        ctx.lineWidth = 1;
        for (let x = 0; x < W; x += 64) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
        for (let y = 0; y < H; y += 64) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
        ctx.fillStyle = '#f49d86';
        ctx.fillRect(0, 0, W, 10);
        ctx.font = `bold 34px ${FONT}`;
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'left';
        ctx.fillText('INFLUIO TV', 48, 66);
        ctx.font = `600 26px ${FONT}`;
        ctx.fillStyle = '#a9cbc9';
        ctx.fillText(title, 285, 66);
        ctx.textAlign = 'right';
        ctx.fillStyle = '#ffd166';
        ctx.fillText('KARACA × INFLUIO', W - 48, 66);
        ctx.textAlign = 'left';
        ctx.fillStyle = 'rgba(255,255,255,0.12)';
        ctx.fillRect(48, 90, W - 96, 2);
    };
    const bigNumber = (label, value, x, y, color = '#ffffff') => {
        ctx.font = `600 28px ${FONT}`;
        ctx.fillStyle = '#b5cecd';
        ctx.fillText(label, x, y);
        ctx.font = `bold 96px ${FONT}`;
        ctx.fillStyle = color;
        ctx.fillText(value, x, y + 100);
    };
    const stat = (label, value, x, y, color = '#ffffff') => {
        ctx.font = `600 26px ${FONT}`;
        ctx.fillStyle = '#b5cecd';
        ctx.fillText(label, x, y);
        ctx.font = `bold 54px ${FONT}`;
        ctx.fillStyle = color;
        ctx.fillText(value, x, y + 60);
    };
    const delta = (label, pct, x, y) => {
        const up = pct === null || pct === undefined ? null : pct >= 0;
        ctx.font = `600 26px ${FONT}`;
        ctx.fillStyle = '#b5cecd';
        ctx.fillText(label, x, y);
        ctx.font = `bold 54px ${FONT}`;
        ctx.fillStyle = up === null ? '#ffffff' : up ? '#4ade80' : '#fb7185';
        ctx.fillText(up === null ? '—' : `${up ? '▲' : '▼'} %${Math.abs(pct)}`, x, y + 60);
    };
    // white plate with the packshot fitted inside; catalog shots are white-background
    const photo = (item, x, y, w, h) => {
        ctx.fillStyle = '#ffffff';
        roundRect(ctx, x, y, w, h, 16);
        ctx.fill();
        const img = item?.image && images.get(item.image);
        if (!img) {
            ctx.fillStyle = '#c9cde6';
            ctx.font = `bold ${Math.round(h / 6)}px ${FONT}`;
            ctx.textAlign = 'center';
            ctx.fillText('KARACA', x + w / 2, y + h / 2 + h / 16);
            ctx.textAlign = 'left';
            return;
        }
        const pad = Math.round(h * 0.06);
        const s = Math.min((w - pad * 2) / img.naturalWidth, (h - pad * 2) / img.naturalHeight);
        const dw = img.naturalWidth * s, dh = img.naturalHeight * s;
        ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
    };
    const badge = (i, x, y) => {
        ctx.fillStyle = RANK[i] || '#ffffff';
        ctx.beginPath();
        ctx.arc(x, y, 28, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#14282d';
        ctx.font = `bold 34px ${FONT}`;
        ctx.textAlign = 'center';
        ctx.fillText(String(i + 1), x, y + 12);
        ctx.textAlign = 'left';
    };
    const row = (i, y, main, right, sub, thumb) => {
        ctx.fillStyle = i % 2 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.09)';
        ctx.fillRect(48, y - 62, W - 96, 116);
        ctx.fillStyle = RANK[i] || '#fff';
        ctx.font = `bold 60px ${FONT}`;
        ctx.fillText(String(i + 1), 72, y + 12);
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold 40px ${FONT}`;
        ctx.fillText(main, 150, y);
        ctx.font = `500 28px ${FONT}`;
        ctx.fillStyle = '#b5cecd';
        ctx.fillText(sub, 150, y + 40);
        if (thumb) photo(thumb, W - 72 - 240 - 104, y - 54, 100, 100);
        ctx.textAlign = 'right';
        ctx.fillStyle = '#4ade80';
        ctx.font = `bold 44px ${FONT}`;
        ctx.fillText(right, W - 72, y + 10);
        ctx.textAlign = 'left';
    };

    const paint = (kind, facts, script) => {
        const f = facts || {};
        const t = f.totals || {};
        const c = f.compare || {};
        const day = f.salesDayLabel || '';
        switch (kind) {
            case 'totals': {
                frame(`GÜNÜN SATIŞI · ${day}`);
                bigNumber('TOPLAM SATIŞ', TL(t.sales), 48, 150);
                bigNumber('SİPARİŞ', String(t.orders ?? 0), 800, 150, '#ffd166');
                // Tune traffic right under the money; null = Tune did not answer in time
                const tr = f.traffic;
                const n = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('tr-TR'));
                stat('TIKLAMA', tr ? n(tr.clicks) : '—', 48, 330, '#35d6ff');
                stat('DÖNÜŞÜM', tr ? n(tr.conversions) : '—', 420, 330, '#35d6ff');
                stat('DÖNÜŞÜM ORANI', tr?.conversionRate != null ? `%${n(tr.conversionRate)}` : '—', 800, 330, '#35d6ff');
                delta('ÖNCEKİ GÜNE GÖRE', c.deltaVsPreviousDayPct, 48, 480);
                delta('7 GÜN ORT.', c.deltaVsAvg7Pct, 420, 480);
                stat('AY BAŞINDAN BERİ', TL(c.monthToDate?.sales), 800, 480);
                ctx.font = `500 28px ${FONT}`;
                ctx.fillStyle = '#a9cbc9';
                ctx.fillText(`${t.activeInfluencers ?? 0} aktif influencer · ${t.installs ?? 0} uygulama indirme`, 48, 660);
                break;
            }
            case 'products': {
                frame(`EN ÇOK SATAN ÜRÜNLER · ${day}`);
                (f.topProducts || []).slice(0, 3).forEach((p, i) => {
                    const x = 48 + i * 400, y = 116, w = 384;
                    ctx.fillStyle = 'rgba(255,255,255,0.07)';
                    roundRect(ctx, x, y, w, 572, 22);
                    ctx.fill();
                    photo(p, x + 16, y + 16, w - 32, 316);
                    badge(i, x + 44, y + 44);
                    ctx.fillStyle = '#ffffff';
                    ctx.font = `bold 30px ${FONT}`;
                    wrap(ctx, shortTitle(p.title) || `Ürün ${p.productId}`, w - 40).slice(0, 2).forEach((line, li) => ctx.fillText(line, x + 20, y + 382 + li * 38));
                    ctx.font = `500 26px ${FONT}`;
                    ctx.fillStyle = '#b5cecd';
                    ctx.fillText(wrap(ctx, `${p.units} adet${p.category ? ` · ${p.category}` : ''}`, w - 40)[0] || '', x + 20, y + 472);
                    ctx.font = `bold 44px ${FONT}`;
                    ctx.fillStyle = '#4ade80';
                    ctx.fillText(TL(p.lineRevenue), x + 20, y + 538);
                });
                break;
            }
            case 'influencers': {
                frame(`GÜNÜN KAHRAMANLARI · ${day}`);
                (f.topInfluencers || []).slice(0, 3).forEach((inf, i) =>
                    row(i, 200 + i * 150, inf.name, TL(inf.sales),
                        `${inf.orders} sipariş${inf.topProduct ? ` · en çok: ${shortTitle(inf.topProduct.title)}` : ''}`, inf.topProduct));
                break;
            }
            case 'tips': {
                frame(`AMPLIFY TIPS · ${day}`);
                let cards = (script?.tips || []).slice(0, 4);
                if (!cards.length) {
                    // a Script without cards (older take): quarter the spoken text
                    const sentences = String((script?.segments || []).find((s) => s.kind === 'tips')?.text || '').split(/(?<=[.!?])\s+/).filter(Boolean);
                    const per = Math.ceil(sentences.length / 4) || 1;
                    cards = Array.from({ length: Math.min(4, sentences.length) }, (_, i) =>
                        ({ type: i < 2 ? 'product' : 'influencer', title: '', text: sentences.slice(i * per, (i + 1) * per).join(' ') }));
                }
                cards.forEach((tip, i) => {
                    const x = 48 + (i % 2) * 600, y = 116 + Math.floor(i / 2) * 292, w = 584, h = 272;
                    const product = tip.type !== 'influencer';
                    ctx.fillStyle = product ? 'rgba(80,120,255,0.18)' : 'rgba(255,45,122,0.18)';
                    roundRect(ctx, x, y, w, h, 18);
                    ctx.fill();
                    ctx.fillStyle = product ? '#7c9cff' : '#f49d86';
                    ctx.fillRect(x, y + 18, 8, h - 36);
                    ctx.font = `bold 22px ${FONT}`;
                    ctx.fillStyle = product ? '#a9cbc9' : '#ff8fb0';
                    ctx.fillText(product ? 'ÜRÜN TIP' : 'INFLUENCER TIP', x + 30, y + 44);
                    let ty = y + 88;
                    if (tip.title) {
                        ctx.font = `bold 30px ${FONT}`;
                        ctx.fillStyle = '#ffffff';
                        ctx.fillText(wrap(ctx, tip.title, w - 60)[0] || '', x + 30, ty);
                        ty += 46;
                    }
                    ctx.font = `500 25px ${FONT}`;
                    ctx.fillStyle = '#e6e9ff';
                    wrap(ctx, tip.text, w - 60).slice(0, 4).forEach((line, li) => ctx.fillText(line, x + 30, ty + li * 34));
                });
                break;
            }
            case 'closing': {
                frame('YARIN GÖRÜŞÜRÜZ');
                ctx.font = `bold 110px ${FONT}`;
                ctx.fillStyle = '#ffffff';
                ctx.textAlign = 'center';
                ctx.fillText('KARACA × INFLUIO', W / 2, 330);
                ctx.font = `600 40px ${FONT}`;
                ctx.fillStyle = '#ffd166';
                ctx.fillText('birlikte büyüyoruz', W / 2, 410);
                ctx.font = `500 30px ${FONT}`;
                ctx.fillStyle = '#a9cbc9';
                ctx.fillText(day, W / 2, 560);
                ctx.textAlign = 'left';
                break;
            }
            default: {
                frame('GÜNLÜK KARACA BÜLTENİ');
                ctx.font = `bold 120px ${FONT}`;
                ctx.fillStyle = '#ffffff';
                ctx.textAlign = 'center';
                ctx.fillText('LIA', W / 2, 330);
                ctx.font = `600 40px ${FONT}`;
                ctx.fillStyle = '#f49d86';
                const whose = f.daysAgo === 1 ? 'DÜNÜN' : f.daysAgo === 0 ? 'BUGÜNÜN (ŞU ANA KADAR)' : 'GÜNÜN';
                ctx.fillText(`CANLI · ${whose} TAM ÖZETİ`, W / 2, 410);
                ctx.font = `500 34px ${FONT}`;
                ctx.fillStyle = '#a9cbc9';
                ctx.fillText(day ? `${day} · 00:00–23:59` : 'Tarih seçin ve script üretin', W / 2, 560);
                ctx.textAlign = 'left';
            }
        }
        tex.needsUpdate = true;
    };
    paint('opening', null, null);
    return { tex, paint, prepare };
}

/* ---------- framed abstract Influio poster ---------- */

function makeInfluioPoster() {
    const canvas = document.createElement('canvas');
    canvas.width = 600; canvas.height = 760;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#f7f0e3';
    ctx.fillRect(0, 0, 600, 760);
    const polygon = (color, points) => {
        ctx.fillStyle = color;
        ctx.beginPath();
        points.forEach(([x, y], i) => i ? ctx.lineTo(x, y) : ctx.moveTo(x, y));
        ctx.closePath();
        ctx.fill();
    };
    // Two interlocking voices create a shared spark: a story amplified through connection.
    ctx.fillStyle = '#eadbbf';
    ctx.beginPath(); ctx.arc(300, 307, 175, 0, Math.PI * 2); ctx.fill();
    polygon('#254f51', [[81, 224], [123, 174], [281, 174], [329, 224], [329, 324], [281, 372], [179, 372], [117, 428], [127, 365], [81, 324]]);
    polygon('#487773', [[81, 224], [123, 174], [281, 174], [235, 224], [235, 324], [179, 372], [127, 365], [81, 324]]);
    polygon('#d78468', [[271, 279], [319, 231], [477, 231], [519, 279], [519, 379], [473, 420], [481, 478], [419, 428], [319, 428], [271, 379]]);
    polygon('#edb299', [[319, 231], [477, 231], [519, 279], [368, 279], [319, 328], [271, 328], [271, 279]]);
    // A warm four-point spark sits where the two speech forms meet.
    polygon('#f7f0e3', [[300, 251], [321, 308], [376, 329], [321, 350], [300, 407], [279, 350], [224, 329], [279, 308]]);
    polygon('#e9b76f', [[300, 286], [311, 318], [341, 329], [311, 340], [300, 372], [289, 340], [259, 329], [289, 318]]);
    ctx.strokeStyle = '#b99051'; ctx.lineWidth = 5; ctx.lineCap = 'round';
    for (const [x1, y1, x2, y2] of [[300, 119, 300, 142], [351, 135, 340, 155], [249, 135, 260, 155]]) {
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
    }
    ctx.fillStyle = '#254f51'; ctx.textAlign = 'center';
    ctx.font = `800 48px ${FONT}`;
    ctx.fillText('influio', 300, 86);
    ctx.font = `bold 49px ${FONT}`;
    ctx.fillText('Hikâyeni', 300, 577);
    ctx.fillText('Güçlü Seslerle', 300, 638);
    ctx.fillText('Anlat.', 300, 699);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

/* ---------- the phone: a live chat that keeps scrolling ---------- */

const CHAT_NAMES = ['karaca.fan', 'mert_k', 'ayse.ecem', 'sude', 'burakk', 'elif__', 'kaan.t', 'zeynep', 'deniz', 'can.y'];
const CHAT_LINES = ['lia bugün ne var', 'hype!!', 'ürünleri göster', 'çay makinesi 😍', 'rakamlar?', 'influio 🔥', 'selam lia',
    'espresso makinesi bomba', 'yarın da yayın var mı', 'karaca x influio ❤️', 'tips kısmını bekliyorum', 'kahramanlar kim', 'wow', 'gg'];
function makeChatScreen() {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 540;
    const ctx = canvas.getContext('2d');
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    const feed = [];
    let viewers = 1180;
    const paint = () => {
        const g = ctx.createLinearGradient(0, 0, 0, canvas.height);
        g.addColorStop(0, '#233d40');
        g.addColorStop(1, '#0d0b1c');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#f49d86';
        ctx.beginPath();
        ctx.arc(24, 30, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.font = `bold 20px ${FONT}`;
        ctx.fillStyle = '#ffffff';
        ctx.fillText('CANLI', 40, 37);
        ctx.textAlign = 'right';
        ctx.fillStyle = '#a9cbc9';
        ctx.font = `600 18px ${FONT}`;
        ctx.fillText(`👁 ${viewers.toLocaleString('tr-TR')}`, canvas.width - 16, 37);
        ctx.textAlign = 'left';
        ctx.fillStyle = 'rgba(255,255,255,0.12)';
        ctx.fillRect(12, 52, canvas.width - 24, 2);
        let y = canvas.height - 24;
        for (let i = feed.length - 1; i >= 0 && y > 80; i--) {
            const m = feed[i];
            ctx.font = `500 17px ${FONT}`;
            const lines = wrap(ctx, m.l, canvas.width - 60);
            const h = 30 + lines.length * 20;
            y -= h;
            ctx.fillStyle = i % 2 ? 'rgba(255,255,255,0.08)' : 'rgba(124,156,255,0.16)';
            roundRect(ctx, 12, y, canvas.width - 24, h - 6, 10);
            ctx.fill();
            ctx.fillStyle = m.c;
            ctx.font = `bold 15px ${FONT}`;
            ctx.fillText(m.n, 22, y + 20);
            ctx.fillStyle = '#e6e9ff';
            ctx.font = `500 17px ${FONT}`;
            for (let li = 0; li < lines.length; li++) ctx.fillText(lines[li], 22, y + 42 + li * 20);
        }
        tex.needsUpdate = true;
    };
    const tick = () => {
        feed.push({
            n: CHAT_NAMES[Math.floor(Math.random() * CHAT_NAMES.length)],
            l: CHAT_LINES[Math.floor(Math.random() * CHAT_LINES.length)],
            c: ['#ff8fb0', '#a9cbc9', '#ffd166', '#4ade80'][Math.floor(Math.random() * 4)],
        });
        if (feed.length > 10) feed.shift();
        viewers = Math.max(900, viewers + Math.round((Math.random() - 0.42) * 20));
        paint();
    };
    for (let i = 0; i < 6; i++) tick();
    return { tex, tick };
}

/* ---------- the director ---------- */

const SHOTS = {
    wide: { pos: [0.7, 2.65, 5.5], look: [0.15, 1.65, -1.8], fov: 43 },
    wideL: { pos: [-1.0, 2.45, 5.1], look: [0.2, 1.65, -1.8], fov: 44 },
    medium: { pos: [0.05, 1.8, 1.25], look: [0, 1.62, -1.35], fov: 36 },
    mediumR: { pos: [0.7, 1.75, 1.1], look: [0.05, 1.6, -1.35], fov: 36 },
    close: { pos: [0.18, 1.92, 0.15], look: [0, 1.84, -1.35], fov: 28 },
    closeL: { pos: [-0.35, 1.9, 0.25], look: [0, 1.84, -1.35], fov: 30 },
    screen: { pos: [1.7, 2.25, 0.2], look: [SCREEN_POS.x, SCREEN_POS.y, SCREEN_POS.z], fov: 36 },
    // fills the frame with the whole board; the boards are dense, a crop loses numbers
    screenTight: { pos: [2.15, 2.25, -0.4], look: [SCREEN_POS.x, SCREEN_POS.y, SCREEN_POS.z], fov: 42 },
    // over Lia's shoulder at the wall screen: high enough that her head sits in the
    // bottom-left corner and the cards stay readable
    ots: { pos: [-0.9, 2.45, -0.3], look: [1.9, 2.2, -3.05], fov: 40 },
    otsPush: { pos: [-0.4, 2.4, -0.6], look: [2.0, 2.2, -3.05], fov: 36 },
    two: { pos: [1.0, 1.95, 2.0], look: [1.0, 1.85, -2.2], fov: 44 },
    twoPush: { pos: [0.9, 1.9, 1.3], look: [1.0, 1.85, -2.2], fov: 42 },
    low: { pos: [-1.3, 1.15, 1.5], look: [0, 1.7, -1.35], fov: 34 },
};
// each entry: [from, to, seconds] — a dolly; the same shot twice is a hold with drift
const GRAMMAR = {
    opening: [['wide', 'medium', 6], ['medium', 'close', 9], ['mediumR', 'medium', 10]],
    totals: [['screenTight', 'screen', 5], ['two', 'medium', 8], ['close', 'closeL', 8]],
    products: [['ots', 'otsPush', 7], ['screen', 'screenTight', 7], ['medium', 'mediumR', 8]],
    influencers: [['closeL', 'close', 6], ['screen', 'two', 7], ['medium', 'medium', 8]],
    // the longest Segment: four tips, so the sentence is longer before it starts alternating
    tips: [['low', 'medium', 7], ['screen', 'screenTight', 7], ['two', 'twoPush', 8], ['close', 'closeL', 8], ['ots', 'otsPush', 8], ['mediumR', 'medium', 8]],
    closing: [['medium', 'wide', 9], ['wideL', 'wide', 10]],
    idle: [['wide', 'wideL', 14], ['wideL', 'wide', 14]],
};

function createDirector(camera) {
    let list = GRAMMAR.idle;
    let index = 0;
    let started = 0;
    const from = new THREE.Vector3(), to = new THREE.Vector3();
    const lookFrom = new THREE.Vector3(), lookTo = new THREE.Vector3();
    const look = new THREE.Vector3();
    let fovFrom = 40, fovTo = 40;
    const load = (i, now) => {
        const [a, b, secs] = list[Math.min(i, list.length - 1)];
        from.fromArray(SHOTS[a].pos); to.fromArray(SHOTS[b].pos);
        lookFrom.fromArray(SHOTS[a].look); lookTo.fromArray(SHOTS[b].look);
        fovFrom = SHOTS[a].fov; fovTo = SHOTS[b].fov;
        started = now;
        return secs;
    };
    let secs = load(0, 0);
    return {
        cut(kind, now) {
            list = GRAMMAR[kind] || GRAMMAR.idle;
            index = 0;
            secs = load(0, now);
        },
        update(now) {
            let k = (now - started) / secs;
            if (k >= 1) {
                // the list is a sentence, not a loop: after the last move, alternate the
                // last two shots so a long Segment never freezes the camera
                index = index + 1 < list.length ? index + 1 : Math.max(0, list.length - 2);
                secs = load(index, now);
                k = 0;
            }
            const e = ease(clamp01(k));
            camera.position.lerpVectors(from, to, e);
            look.lerpVectors(lookFrom, lookTo, e);
            camera.position.x += Math.sin(now * 0.9) * 0.012;
            camera.position.y += Math.sin(now * 1.3 + 1) * 0.008;
            look.y += Math.sin(now * 0.7) * 0.01;
            camera.fov = fovFrom + (fovTo - fovFrom) * e;
            camera.updateProjectionMatrix();
            camera.lookAt(look);
        },
    };
}

/* ---------- Lia's arms: joint angles per pose ---------- */

// sx/sz: shoulder pitch/roll, ex/ez: elbow pitch/roll (roll is mirrored per side).
// rest = forearms on the desk; raise = hand up at chest height; open = palm-up "look at
// this"; wave = arm out to the side, the wrist does the waving.
const POSES = {
    rest: { sx: -0.32, sz: 0.12, ex: -1.3, ez: -0.3 },
    raise: { sx: -0.55, sz: 0.25, ex: -2.35, ez: -0.5 },
    open: { sx: -0.45, sz: 0.3, ex: -1.9, ez: -0.2 },
    wave: { sx: -0.5, sz: 0.95, ex: -2.4, ez: 0.1 },
};
// [right, left] pose per gesture burst
const GESTURES = { R: ['raise', 'rest'], L: ['rest', 'raise'], both: ['open', 'open'], point: ['raise', 'open'], rest: ['rest', 'rest'] };

/* ---------- the scene ---------- */

export function createStudioScene(mount, { mouthLevel = () => 0, loadImage = null } = {}) {
    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(1);
    renderer.setSize(STAGE_W, STAGE_H, false);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    const canvas = renderer.domElement;
    canvas.style.width = '100%';
    canvas.style.height = 'auto';
    canvas.style.display = 'block';
    canvas.style.borderRadius = '12px';
    mount.appendChild(canvas);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(BG);
    scene.fog = new THREE.Fog(BG, 9, 18);
    const camera = new THREE.PerspectiveCamera(40, STAGE_W / STAGE_H, 0.1, 40);
    camera.position.set(1.2, 2.2, 3.9);

    /* lights */
    scene.add(new THREE.HemisphereLight(0xe3eeff, 0x514438, 1.25));
    const key = new THREE.SpotLight(0xffe8d6, 32, 14, Math.PI / 5, 0.5, 1.4);
    key.position.set(1.4, 3.4, 1.6);
    key.target.position.copy(LIA_POS).setY(1.5);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.normalBias = 0.025;
    key.shadow.bias = -0.0001;
    scene.add(key, key.target);
    const rimPink = new THREE.PointLight(0xff786d, 5, 9, 1.6);
    rimPink.position.set(-2.4, 2.6, -2.4);
    const rimCyan = new THREE.PointLight(0x65cfc0, 5, 9, 1.6);
    rimCyan.position.set(2.8, 2.7, -2.2);
    const screenGlow = new THREE.PointLight(0x99c8d0, 2, 6, 1.5);
    screenGlow.position.copy(SCREEN_POS).add(new THREE.Vector3(-0.4, -0.2, 0.9));
    const ringLight = new THREE.PointLight(0xfff1dc, 6, 5, 1.6);
    ringLight.position.set(-2.5, 2.7, 0.3);
    scene.add(rimPink, rimCyan, screenGlow, ringLight);

    /* room */
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(16, 16), flat(0x30383b));
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    scene.add(floor);
    const rug = new THREE.Mesh(new THREE.CircleGeometry(2.65, 12), flat(0x637778));
    rug.rotation.x = -Math.PI / 2;
    rug.position.set(0.2, 0.005, -1.2);
    rug.receiveShadow = true;
    scene.add(rug);
    const backWall = box(scene, 16, 7, 0.2, flat(0x202e32), 0, 3.5, -3.3);
    backWall.receiveShadow = true;
    box(scene, 0.2, 7, 16, flat(0x28383b), -5.2, 3.5, 2);
    // Architectural walnut slats and inset acoustic fabric frame the broadcast wall.
    const walnut = flat(0x75503c), charcoal = flat(0x18262b), brass = flat(0xd6b77b, { metalness: 0.65, roughness: 0.4 });
    box(scene, 3.65, 3.8, 0.1, charcoal, -2.55, 1.95, -3.16);
    for (let i = 0; i < 27; i++) box(scene, 0.065, 3.75, 0.09, walnut, -4.28 + i * 0.13, 1.95, -3.06);
    box(scene, 4.1, 2.5, 0.08, charcoal, SCREEN_POS.x, SCREEN_POS.y, -3.18);
    const strips = [];
    for (const [w, h, x, y, color] of [
        [8.6, 0.025, 0, 0.12, 0xffc38d],
        [0.025, 3.75, -0.66, 1.95, 0xff967b],
        [3.8, 0.025, SCREEN_POS.x, 1.13, 0x79d8ca],
    ]) strips.push(box(scene, w, h, 0.03, glow(color, 1.3), x, y, -2.99));
    // A quiet geometric acoustic pattern, kept clear of the information screen.
    for (let i = 0; i < 5; i++) {
        const panel = box(scene, 0.38, 0.38, 0.08, flat(i % 2 ? 0x426063 : 0x344b50), -0.32 + i * 0.48, 3.68, -3.1);
        panel.rotation.z = Math.PI / 4;
    }
    // neon sign
    const neon = document.createElement('canvas');
    neon.width = 1024; neon.height = 256;
    const nctx = neon.getContext('2d');
    nctx.font = `bold 132px ${FONT}`;
    nctx.textAlign = 'center';
    nctx.shadowColor = '#ffac8c'; nctx.shadowBlur = 12;
    nctx.fillStyle = '#ffe5cc';
    nctx.fillText('LIA / DAILY', 512, 156);
    nctx.shadowBlur = 0;
    nctx.font = `500 30px ${FONT}`;
    nctx.fillText('THE INFLUIO CREATOR REPORT', 512, 220);
    const neonTex = new THREE.CanvasTexture(neon);
    neonTex.colorSpace = THREE.SRGBColorSpace;
    const neonMesh = new THREE.Mesh(new THREE.PlaneGeometry(2.6, 0.65), new THREE.MeshBasicMaterial({ map: neonTex, transparent: true }));
    neonMesh.position.set(-2.45, 3.18, -2.945);
    scene.add(neonMesh);
    // Framed abstract poster beneath the title; shared desk and jacket logo.
    const logoMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0 });
    new THREE.TextureLoader().load(LOGO_URL, (tex) => { tex.colorSpace = THREE.SRGBColorSpace; logoMat.map = tex; logoMat.opacity = 1; logoMat.needsUpdate = true; });
    box(scene, 1.22, 1.52, 0.07, brass, -2.45, 2.03, -2.99);
    const poster = new THREE.Mesh(new THREE.PlaneGeometry(1.14, 1.444), new THREE.MeshBasicMaterial({ map: makeInfluioPoster() }));
    poster.position.set(-2.45, 2.03, -2.945);
    scene.add(poster);
    const deskPlate = new THREE.Mesh(new THREE.PlaneGeometry(0.96, 0.24), logoMat);
    deskPlate.position.set(0, 0.5, -0.275);
    scene.add(deskPlate);
    // wall screen
    const board = makeBoard(loadImage);
    box(scene, SCREEN_W + 0.12, SCREEN_H + 0.12, 0.08, flat(0x0b0a18, { roughness: 0.4, metalness: 0.3 }), SCREEN_POS.x, SCREEN_POS.y, SCREEN_POS.z - 0.05);
    const screen = new THREE.Mesh(new THREE.PlaneGeometry(SCREEN_W, SCREEN_H), new THREE.MeshBasicMaterial({ map: board.tex }));
    screen.position.copy(SCREEN_POS);
    scene.add(screen);
    // shelves left with plants, books, a trophy
    for (const y of [1.55, 2.35]) box(scene, 1.6, 0.05, 0.36, walnut, -4.0, y, -2.95);
    for (const [x, y, c] of [[-4.55, 1.72, 0xff2d7a], [-4.4, 1.72, 0x35d6ff], [-4.26, 1.72, 0xffd166], [-3.55, 2.52, 0x7c9cff], [-3.4, 2.52, 0xff2d7a]])
        box(scene, 0.09, 0.3, 0.24, flat(c), x, y, -2.95);
    for (const [x, y] of [[-3.55, 1.6], [-4.45, 2.4]]) {
        cyl(scene, 0.11, 0.09, 0.2, flat(0xd98c5f), x, y + 0.1, -2.95);
        sphere(scene, 0.19, flat(0x3bb273), x, y + 0.36, -2.95, 10, 8);
    }
    cyl(scene, 0.08, 0.1, 0.2, flat(0xffd166, { metalness: 0.8, roughness: 0.3 }), -4.0, 2.5, -2.95);
    // gaming chair behind Lia
    const chair = new THREE.Group();
    chair.position.set(0, 0, -1.75);
    scene.add(chair);
    box(chair, 0.62, 0.12, 0.6, flat(0x14121f), 0, 0.5, 0);
    box(chair, 0.66, 1.15, 0.14, flat(0x14121f), 0, 1.1, -0.3);
    box(chair, 0.16, 1.05, 0.15, flat(0x659b96), -0.24, 1.08, -0.29);
    box(chair, 0.16, 1.05, 0.15, flat(0x659b96), 0.24, 1.08, -0.29);
    box(chair, 0.34, 0.16, 0.12, flat(0x1d1a2e), 0, 1.72, -0.26);
    cyl(chair, 0.04, 0.04, 0.5, flat(0x2a2740), 0, 0.25, 0);
    for (let i = 0; i < 5; i++) {
        const leg = box(chair, 0.34, 0.03, 0.05, flat(0x2a2740), 0, 0.03, 0);
        leg.rotation.y = (i / 5) * Math.PI * 2;
        leg.position.x = Math.cos((i / 5) * Math.PI * 2) * 0.16;
        leg.position.z = Math.sin((i / 5) * Math.PI * 2) * 0.16;
    }
    // Broadcast desk with mat, phone, articulated microphone and creator controls.
    const desk = new THREE.Group();
    desk.position.set(0, 0, -0.85);
    scene.add(desk);
    box(desk, 3.3, 0.07, 1.1, flat(0x9b7354, { roughness: 0.72 }), 0, 0.92, 0);
    box(desk, 3.3, 0.12, 0.06, flat(0x1a1430), 0, 0.85, 0.52);
    for (const x of [-1.5, 1.5]) box(desk, 0.08, 0.9, 0.9, flat(0x1a1430), x, 0.45, 0);
    box(desk, 1.6, 0.012, 0.6, flat(0x120f22), 0.1, 0.962, 0.05);
    // Frosted white glass front with the Influio logo floating above its surface.
    const deskGlass = box(desk, 2.85, 0.65, 0.035, new THREE.MeshPhysicalMaterial({
        color: 0xffffff, transparent: true, opacity: 0.9, roughness: 0.38,
        metalness: 0, clearcoat: 1, clearcoatRoughness: 0.08, depthWrite: false,
    }), 0, 0.5, 0.54);
    deskGlass.castShadow = false;
    for (const y of [0.175, 0.825]) box(desk, 2.88, 0.018, 0.04, brass, 0, y, 0.54);
    box(desk, 3.22, 0.025, 0.035, glow(0xffb18d, 1.15), 0, 0.86, 0.565);
    for (const x of [-1.5, 1.5]) box(desk, 0.085, 0.1, 0.91, brass, x, 0.06, 0);
    box(desk, 0.65, 0.035, 0.23, flat(0xd5dbd6), -0.05, 0.99, 0.18);
    for (let row = 0; row < 3; row++) for (let col = 0; col < 11; col++)
        box(desk, 0.045, 0.008, 0.046, flat(col === 10 ? 0xe99c84 : 0x667d7d), -0.32 + col * 0.053, 1.012, 0.11 + row * 0.063);
    box(desk, 0.28, 0.04, 0.23, charcoal, 0.65, 0.99, 0.15);
    for (let row = 0; row < 2; row++) for (let col = 0; col < 3; col++)
        box(desk, 0.062, 0.008, 0.065, glow(row ? 0x72c9bb : 0xffaa8c, 0.55), 0.57 + col * 0.08, 1.015, 0.1 + row * 0.09);
    // the phone: chat scrolls on it, screen turned to the front-right so the shots read it
    const chat = makeChatScreen();
    const phone = new THREE.Group();
    phone.position.set(-0.78, 0.955, -0.12);
    phone.rotation.y = 0.45;
    desk.add(phone);
    cyl(phone, 0.07, 0.085, 0.014, flat(0x0c0a18, { metalness: 0.4, roughness: 0.4 }), 0, 0.007, 0);
    const holder = box(phone, 0.05, 0.14, 0.012, flat(0x0c0a18), 0, 0.08, -0.035);
    holder.rotation.x = -0.35;
    const phoneBody = new THREE.Group();
    phoneBody.position.set(0, 0.11, 0);
    phoneBody.rotation.x = -0.35;
    phone.add(phoneBody);
    box(phoneBody, 0.08, 0.166, 0.008, flat(0x14121f, { metalness: 0.5, roughness: 0.3 }), 0, 0, 0);
    const phoneScreen = new THREE.Mesh(new THREE.PlaneGeometry(0.072, 0.152), new THREE.MeshBasicMaterial({ map: chat.tex }));
    phoneScreen.position.z = 0.0045;
    phoneBody.add(phoneScreen);
    const phoneGlow = new THREE.PointLight(0x9fb0ff, 0.6, 1.0, 1.5);
    phoneGlow.position.set(0, 0.16, 0.12);
    phone.add(phoneGlow);
    // mic on a boom from the right
    const boom = new THREE.Group();
    boom.position.set(1.35, 0.96, 0.1);
    desk.add(boom);
    cyl(boom, 0.05, 0.07, 0.05, flat(0x0c0a18), 0, 0.02, 0);
    const boomPoints = [[0, 0.04, 0], [-0.28, 0.67, -0.16], [-1.05, 0.68, -0.42]];
    for (let i = 1; i < boomPoints.length; i++) {
        const start = new THREE.Vector3(...boomPoints[i - 1]);
        const end = new THREE.Vector3(...boomPoints[i]);
        const link = cyl(boom, 0.018, 0.018, start.distanceTo(end), charcoal);
        link.position.copy(start).lerp(end, 0.5);
        link.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), end.clone().sub(start).normalize());
        sphere(boom, 0.035, charcoal, ...boomPoints[i], 8, 6);
    }
    const micHead = cyl(boom, 0.045, 0.045, 0.16, flat(0x0c0a18, { roughness: 0.4, metalness: 0.5 }), -1.05, 0.68, -0.42);
    micHead.rotation.z = 1.1;
    box(boom, 0.03, 0.03, 0.03, glow(0xff2d7a, 2.5), -1.0, 0.62, -0.42);
    // Ceramic coffee cup, coaster and handle.
    cyl(desk, 0.12, 0.12, 0.012, walnut, -1.15, 0.967, 0.26, 12);
    cyl(desk, 0.073, 0.06, 0.16, flat(0xe9ded0), -1.15, 1.05, 0.26, 10);
    cyl(desk, 0.063, 0.063, 0.003, flat(0x422a20), -1.15, 1.132, 0.26, 10);
    const handle = new THREE.Mesh(new THREE.TorusGeometry(0.052, 0.014, 4, 8), flat(0xe9ded0));
    handle.position.set(-1.235, 1.06, 0.26);
    desk.add(handle);
    // Sculpted foliage: individual angular leaves instead of stacked green balls.
    for (const [x, z, size] of [[3.95, -2.1, 1], [-3.5, -1.65, 0.72]]) {
        cyl(scene, 0.25 * size, 0.19 * size, 0.45 * size, flat(0xd7c5a9), x, 0.225 * size, z, 8);
        for (let i = 0; i < 9; i++) {
            const angle = i * 2.4;
            const height = (0.7 + (i % 3) * 0.27) * size;
            const stem = cyl(scene, 0.012, 0.015, height, flat(0x456b50), x, 0.4 * size + height / 2, z, 5);
            stem.rotation.z = Math.sin(angle) * 0.25;
            const leaf = new THREE.Mesh(new THREE.OctahedronGeometry(1), flat(i % 2 ? 0x5a9476 : 0x2f715e));
            leaf.scale.set(0.18 * size, 0.42 * size, 0.065 * size);
            leaf.position.set(x + Math.sin(angle) * 0.27 * size, height + 0.35 * size, z + Math.cos(angle) * 0.2 * size);
            leaf.rotation.set(0.3, angle, -Math.sin(angle) * 0.7);
            leaf.castShadow = true;
            scene.add(leaf);
        }
    }
    // Creator award on the shelf: a sculptural play button in a brushed-metal frame.
    box(scene, 0.36, 0.46, 0.05, brass, -3.93, 1.81, -2.91);
    box(scene, 0.29, 0.38, 0.025, charcoal, -3.93, 1.81, -2.87);
    const award = cyl(scene, 0.085, 0.085, 0.02, brass, -3.93, 1.83, -2.84, 3);
    award.rotation.x = Math.PI / 2;
    // "CANLI" lamp on the wall — on only while a Take is rolling
    const liveLamp = box(scene, 0.55, 0.16, 0.05, flat(0x2b0a1a, { emissive: 0xff2d7a, intensity: 0 }), SCREEN_POS.x - SCREEN_W / 2 + 0.3, SCREEN_POS.y + SCREEN_H / 2 + 0.4, -3.18);

    /* Lia: jointed low-poly creator rig inside the optional GLB slot */
    const lia = new THREE.Group();
    lia.position.copy(LIA_POS);
    scene.add(lia);
    const ph = new THREE.Group();
    lia.add(ph);
    const SKIN = 0xe9b18c, HOODIE = 0x397b79, HOODIE_DARK = 0x215756, HAIR = 0x392b30, DYE = 0xd88670, JEANS = 0x293a46;
    const skinMat = flat(SKIN), hoodieMat = flat(HOODIE), jeansMat = flat(JEANS);
    // seated: hips on the chair, thighs under the desk, sneakers on the rug
    sphere(ph, 0.23, jeansMat, 0, 0.72, -0.24, 16, 12).scale.set(1.2, 0.7, 1);
    for (const side of [-1, 1]) {
        capsule(ph, 0.1, 0.36, jeansMat, side * 0.13, 0.66, 0.05).rotation.x = -Math.PI / 2 + 0.2;
        capsule(ph, 0.08, 0.36, jeansMat, side * 0.14, 0.36, 0.33).rotation.x = -0.12;
        box(ph, 0.13, 0.09, 0.27, flat(0xf5f5f5), side * 0.14, 0.065, 0.4);
        box(ph, 0.135, 0.03, 0.272, flat(DYE), side * 0.14, 0.03, 0.4);
    }
    // upper body sways and leans as one; the head rides on it
    const upper = new THREE.Group();
    ph.add(upper);
    capsule(upper, 0.21, 0.16, hoodieMat, 0, 0.98, -0.1).scale.set(1.1, 1, 0.8);
    capsule(upper, 0.26, 0.34, hoodieMat, 0, 1.3, -0.02).scale.set(1.1, 1, 0.74);
    capsule(upper, 0.11, 0.34, hoodieMat, 0, 1.57, -0.02).rotation.z = Math.PI / 2; // shoulders
    // Cropped teal varsity jacket over a cream shirt, with tailored collar and seams.
    box(upper, 0.16, 0.39, 0.025, flat(0xf0e0c9), 0, 1.39, 0.182);
    for (const side of [-1, 1]) {
        const collar = box(upper, 0.105, 0.17, 0.04, flat(HOODIE_DARK), side * 0.093, 1.58, 0.165);
        collar.rotation.z = side * -0.35;
        box(upper, 0.012, 0.31, 0.015, brass, side * 0.097, 1.35, 0.205);
        box(upper, 0.09, 0.018, 0.022, flat(HOODIE_DARK), side * 0.19, 1.31, 0.178).rotation.z = side * 0.15;
    }
    const chestLogo = new THREE.Mesh(new THREE.PlaneGeometry(0.12, 0.03), logoMat);
    chestLogo.position.set(-0.185, 1.46, 0.185);
    upper.add(chestLogo);
    cyl(upper, 0.075, 0.085, 0.16, skinMat, 0, 1.7, 0.02, 10); // neck
    // arms: shoulder → upper arm → elbow → forearm → wrist → palm + fingers
    const arms = {};
    for (const side of [-1, 1]) {
        const shoulder = joint(upper, side * 0.3, 1.56, -0.02);
        capsule(shoulder, 0.08, 0.34, hoodieMat, 0, -0.25, 0);
        const elbow = joint(shoulder, 0, -0.5, 0);
        capsule(elbow, 0.07, 0.3, hoodieMat, 0, -0.2, 0);
        const cuff = new THREE.Mesh(new THREE.TorusGeometry(0.072, 0.02, 8, 16), flat(HOODIE_DARK));
        cuff.position.y = -0.38;
        cuff.rotation.x = Math.PI / 2;
        elbow.add(cuff);
        const wrist = joint(elbow, 0, -0.44, 0);
        sphere(wrist, 0.07, skinMat, 0, -0.03, 0, 12, 10).scale.set(1, 1.2, 0.5);
        for (let i = 0; i < 4; i++) capsule(wrist, 0.015, 0.05, skinMat, (i - 1.5) * 0.03, -0.12, 0).rotation.x = 0.2;
        const thumb = capsule(wrist, 0.015, 0.045, skinMat, side * -0.075, -0.04, 0.005);
        thumb.rotation.z = side * -0.7;
        arms[side] = { shoulder, elbow, wrist, side, cur: { ...POSES.rest }, target: POSES.rest };
    }
    // head
    const head = new THREE.Group();
    head.position.set(0, HEAD_Y, 0);
    upper.add(head);
    sphere(head, 0.245, skinMat, 0, 0, 0, 16, 12).scale.set(0.91, 1.13, 0.92);
    // A faceted, side-parted bob with copper face-framing strands.
    const hairMat = flat(HAIR);
    const hairCap = new THREE.Mesh(new THREE.SphereGeometry(0.27, 10, 7, 0, Math.PI * 2, 0, Math.PI / 2.35), hairMat);
    hairCap.position.set(0, 0.05, -0.025);
    head.add(hairCap);
    sphere(head, 0.24, hairMat, 0, -0.05, -0.12, 10, 7).scale.set(1.05, 1.45, 0.65);
    for (const side of [-1, 1]) {
        const lock = sphere(head, 0.14, hairMat, side * 0.207, -0.13, -0.015, 7, 5);
        lock.scale.set(0.6, 2.05, 0.95);
        lock.rotation.z = side * 0.12;
        const strand = sphere(head, 0.07, flat(DYE), side * 0.203, -0.18, 0.09, 5, 4);
        strand.scale.set(0.48, 2.5, 0.5);
        strand.rotation.z = side * 0.09;
        sphere(head, 0.05, skinMat, side * 0.224, -0.025, 0, 8, 6).scale.set(0.5, 1, 0.7);
        const earring = new THREE.Mesh(new THREE.TorusGeometry(0.035, 0.008, 4, 10), brass);
        earring.position.set(side * 0.237, -0.115, 0.025);
        head.add(earring);
    }
    const fringeShape = new THREE.Shape();
    fringeShape.moveTo(-0.235, 0.10);
    for (const [x, y] of [[-0.19, 0.23], [-0.06, 0.29], [0.11, 0.255], [0.21, 0.16], [0.09, 0.18], [-0.03, 0.13], [-0.17, 0.075]]) fringeShape.lineTo(x, y);
    fringeShape.closePath();
    const fringe = new THREE.Mesh(new THREE.ExtrudeGeometry(fringeShape, { depth: 0.055, bevelEnabled: false }), hairMat);
    fringe.position.z = 0.155;
    fringe.castShadow = true;
    head.add(fringe);
    const nose = new THREE.Mesh(new THREE.ConeGeometry(0.027, 0.075, 4), skinMat);
    nose.rotation.x = Math.PI / 2;
    nose.position.set(0, -0.025, 0.23);
    head.add(nose);
    const eyes = [];
    for (const side of [-1, 1]) {
        const eye = new THREE.Group();
        eye.position.set(side * 0.085, 0.025, 0.207);
        eye.scale.set(1, 0.72, 0.35);
        head.add(eye);
        sphere(eye, 0.039, new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.4 }), 0, 0, 0, 12, 10);
        sphere(eye, 0.022, flat(0x39665b), 0, 0, 0.028, 10, 8);
        sphere(eye, 0.009, new THREE.MeshBasicMaterial({ color: 0xffffff }), 0.006, 0.008, 0.057, 6, 6);
        sphere(eye, 0.011, flat(0x182528), 0, 0, 0.047, 8, 6);
        box(eye, 0.07, 0.009, 0.012, hairMat, 0, 0.026, 0.023).rotation.z = side * 0.12;
        eyes.push(eye);
        const brow = box(head, 0.1, 0.018, 0.02, flat(HAIR), side * 0.085, 0.1, 0.215);
        brow.rotation.z = side * -0.12;
        const blush = new THREE.Mesh(new THREE.CircleGeometry(0.035, 12), new THREE.MeshBasicMaterial({ color: 0xff8fb0, transparent: true, opacity: 0.25 }));
        blush.position.set(side * 0.14, -0.06, 0.2);
        blush.lookAt(side * 0.6, -0.1, 1);
        head.add(blush);
    }
    const mouth = new THREE.Group();
    mouth.position.set(0, -0.095, 0.215);
    head.add(mouth);
    const lips = sphere(mouth, 1, flat(0xb85d60), 0, 0, 0, 10, 6);
    lips.scale.set(0.044, 0.012, 0.012);
    const mouthOpen = sphere(mouth, 1, flat(0x532d34), 0, -0.006, 0.008, 10, 6);
    mouthOpen.scale.set(0.034, 0.004, 0.008);
    // headset: angular band and compact cups with restrained teal accent rings
    const hsMat = flat(0x111020, { roughness: 0.5, metalness: 0.3 });
    const band = new THREE.Mesh(new THREE.TorusGeometry(0.31, 0.022, 4, 12, Math.PI), hsMat);
    band.position.y = -0.01;
    head.add(band);
    const cupRings = [];
    for (const side of [-1, 1]) {
        cyl(head, 0.06, 0.06, 0.03, flat(0x2a2838), side * 0.27, -0.01, 0).rotation.z = Math.PI / 2; // cushion on the hair
        const cup = cyl(head, 0.082, 0.082, 0.07, hsMat, side * 0.315, -0.01, 0);
        cup.rotation.z = Math.PI / 2;
        const ringR = new THREE.Mesh(new THREE.TorusGeometry(0.065, 0.012, 8, 20), glow(0x77d5bf, 0.8));
        ringR.position.set(side * 0.355, -0.01, 0);
        ringR.rotation.y = Math.PI / 2;
        head.add(ringR);
        cupRings.push(ringR);
    }

    const rig = { head, mouthOpen, lips, eyes, upper, arms };
    new GLTFLoader().load(LIA_GLB, (gltf) => {
        ph.visible = false;
        gltf.scene.traverse((o) => { o.castShadow = true; });
        lia.add(gltf.scene);
        rig.head = gltf.scene.getObjectByName('Head') ?? gltf.scene;
        rig.mouthOpen = gltf.scene.getObjectByName('Mouth') ?? null;
        rig.lips = null;
        rig.eyes = [];
        rig.upper = null;
        rig.arms = null;
    }, undefined, () => { /* no model yet — the placeholder stays */ });

    /* state */
    const director = createDirector(camera);
    let speaking = false;
    let kind = 'opening';
    let last = { facts: null, script: null };
    let level = 0;
    let nextBlink = 2;
    let blinkUntil = 0;
    let nextChat = 1;
    let gestureUntil = 0, nextGesture = 0, waveUntil = 0;
    const clock = new THREE.Clock();
    let raf = 0;
    const headBaseY = HEAD_Y;

    const setGesture = (name) => {
        const [r, l] = GESTURES[name];
        rig.arms[1].target = POSES[r];
        rig.arms[-1].target = POSES[l];
    };
    const frame = () => {
        raf = requestAnimationFrame(frame);
        const t = clock.getElapsedTime();
        // mouth follows the actual audio, smoothed so it reads as speech, not a meter
        const target = speaking ? clamp01(mouthLevel()) : 0;
        level += (target - level) * (target > level ? 0.55 : 0.25);
        if (rig.mouthOpen) rig.mouthOpen.scale.y = rig.upper ? 0.004 + level * 0.035 : 1 + level * 7;
        if (rig.lips) rig.lips.scale.y = 0.012 * Math.max(0.35, 1 - level * 0.6);
        // head: bob while talking, breathe when not; a nod every now and then
        const bob = speaking ? Math.sin(t * 9) * 0.012 * (0.5 + level) : Math.sin(t * 1.4) * 0.006;
        rig.head.position.y = headBaseY + bob;
        rig.head.rotation.z = speaking ? Math.sin(t * 2.7) * 0.05 : Math.sin(t * 0.6) * 0.02;
        rig.head.rotation.y = Math.sin(t * 0.45) * 0.06 + (speaking ? Math.sin(t * 1.9) * 0.05 : 0);
        rig.head.rotation.x = (speaking ? -0.04 + Math.sin(t * 3.3) * 0.03 * level : 0) + Math.sin(t * 0.8) * 0.015;
        if (rig.upper) {
            // the whole upper body breathes, sways, and leans in a little while she talks
            rig.upper.position.y = Math.sin(t * 1.6) * 0.006;
            rig.upper.rotation.y = Math.sin(t * 0.5) * 0.04 + (speaking ? Math.sin(t * 1.7) * 0.03 : 0);
            rig.upper.rotation.x = (speaking ? 0.05 : 0) + Math.sin(t * 1.6) * 0.008;
        }
        if (rig.arms) {
            // gestures come in bursts while she talks; tips is her talk-with-hands Segment;
            // an opening cut gets a wave. Between bursts the forearms go back on the desk.
            if (t < waveUntil) { rig.arms[1].target = POSES.wave; rig.arms[-1].target = POSES.rest; }
            else if (speaking && t > nextGesture) {
                const names = kind === 'tips' ? ['R', 'L', 'both', 'point'] : ['R', 'R', 'L', 'both'];
                setGesture(names[Math.floor(Math.random() * names.length)]);
                gestureUntil = t + (kind === 'tips' ? 2.2 : 1.6) + Math.random() * 1.6;
                nextGesture = gestureUntil + (kind === 'tips' ? 0.4 : 0.8) + Math.random() * 1.8;
            } else if (t > gestureUntil || !speaking) setGesture('rest');
            for (const arm of [rig.arms[-1], rig.arms[1]]) {
                const { cur, target, side } = arm;
                for (const k of ['sx', 'sz', 'ex', 'ez']) cur[k] += (target[k] - cur[k]) * 0.09;
                const active = target !== POSES.rest;
                const beat = active && speaking ? Math.sin(t * 6 + side) * 0.12 * (0.4 + level) : 0;
                arm.shoulder.rotation.set(cur.sx + beat * 0.3, 0, side * cur.sz);
                arm.elbow.rotation.set(cur.ex + beat, 0, side * cur.ez);
                arm.wrist.rotation.z = target === POSES.wave ? Math.sin(t * 11) * 0.4 : 0;
                arm.wrist.rotation.x = active ? 0.25 : 0;
            }
        }
        // blink
        if (t > nextBlink) { blinkUntil = t + 0.12; nextBlink = t + 2.5 + Math.random() * 3; }
        const blink = t < blinkUntil ? 0.06 : 0.72;
        for (const e of rig.eyes) e.scale.y += (blink - e.scale.y) * 0.6;
        // the chat on the phone keeps coming
        if (t > nextChat) { chat.tick(); nextChat = t + 1.4 + Math.random() * 2.4; }
        // Stable brand colors and a slow breathing accent keep skin tones consistent.
        for (const strip of strips) strip.material.emissiveIntensity = 1.15 + Math.sin(t * 0.65) * 0.08;
        for (const ring of cupRings) ring.material.emissiveIntensity = 0.7 + level * 0.35;
        director.update(t);
        renderer.render(scene, camera);
    };
    frame();

    return {
        canvas,
        /** Load the product photos in the facts so the boards can paint them; repaints when done. */
        prepare: (facts) => board.prepare(facts).then(() => board.paint(kind, last.facts || facts, last.script)),
        /** Repaint the wall for a Segment and cut the camera to its grammar. script = { segments, tips }. */
        setSegment(nextKind, facts, script) {
            kind = nextKind;
            last = { facts, script };
            board.paint(nextKind, facts, script);
            director.cut(nextKind, clock.getElapsedTime());
            if (nextKind === 'opening') waveUntil = clock.getElapsedTime() + 2.4;
        },
        /** Repaint only (facts arrived, nobody is talking yet). */
        setBoard(nextKind, facts, script) { last = { facts, script }; board.paint(nextKind, facts, script); },
        speak(on) { speaking = !!on; },
        setLive(on) { liveLamp.material.emissiveIntensity = on ? 2.4 : 0; },
        idle() { kind = 'idle'; director.cut('idle', clock.getElapsedTime()); },
        dispose() {
            cancelAnimationFrame(raf);
            renderer.dispose();
            scene.traverse((o) => { o.geometry?.dispose?.(); if (o.material) (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => m.dispose?.()); });
            if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
        },
    };
}
